import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  HStack,
  Icon,
  IconButton,
  Input,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from "@chakra-ui/react";
import { FaArrowLeft, FaCheck, FaHistory, FaSave, FaTimes } from "react-icons/fa";
import { MdAccountTree, MdCode, MdEdit, MdPlayArrow } from "react-icons/md";
import { LuWorkflow } from "react-icons/lu";
import JobCreationComponent from "./JobCreationComponent";
import { Header } from "build-ts/metadata";
import AnalysisRunsViewer from "./AnalysisRunsViewer";
import { runJobAsync } from "services/ScheduleServices";
import { MetadataTypeEnum, metadataTypeEnumToJSON } from "build-ts/enums";
import showToast from "hooks/useCustomToast";

interface JobViewComponentProps {
  onBack: () => void;
  isCreatingNew?: boolean;
  selectedJob?: Header;
}

const JobViewComponent: React.FC<JobViewComponentProps> = ({
  onBack,
  isCreatingNew = true,
  selectedJob,
}) => {
  const [activeTab, setActiveTab] = useState<number>(0);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [triggerSave, setTriggerSave] = useState<boolean>(false);
  const [triggerDiscard, setTriggerDiscard] = useState<boolean>(false);
  const [hasFormChanges, setHasFormChanges] = useState<boolean>(false);
  const [isRunningWorkflow, setIsRunningWorkflow] = useState(false);

  // Editable job name
  const [jobName, setJobName] = useState<string>(selectedJob?.name?.trim() || "");
  const [isEditingJobName, setIsEditingJobName] = useState(false);
  const [jobNameDraft, setJobNameDraft] = useState("");

  // Reset state when the selected job changes
  useEffect(() => {
    setActiveTab(0);
    setJobName(selectedJob?.name?.trim() || "");
    setIsEditingJobName(false);
    setJobNameDraft("");
  }, [selectedJob?.id]);

  const jobDisplayName = jobName || selectedJob?.name?.trim() || (isCreatingNew ? "New Workflow" : "Workflow");

  const handleSave = () => setTriggerSave(true);
  const handleDiscard = () => {
    setJobName(selectedJob?.name?.trim() || "");
    setTriggerDiscard(true);
  };

  const handleRunJob = async () => {
    if (!selectedJob?.id) return;
    setIsRunningWorkflow(true);
    try {
      await runJobAsync(selectedJob.id, metadataTypeEnumToJSON(MetadataTypeEnum.ANALYST));
      showToast("Analysis running", "The analysis has been triggered.", "success");
    } catch {
      showToast("Run failed", "", "error");
    } finally {
      setIsRunningWorkflow(false);
    }
  };

  const confirmJobNameEdit = () => {
    setJobName(jobNameDraft.trim() || jobDisplayName);
    setIsEditingJobName(false);
  };

  const showFormFooter =
    (activeTab === 0 || activeTab === 1) &&
    (hasFormChanges || isCreatingNew);

  return (
    <Box height="100%" bg="white" display="flex" flexDirection="column">
      {/* Chrome header: back, editable name, tabs, Run */}
      <HStack
        px={3}
        pb={0}
        pt={0.5}
        borderBottom="1px solid"
        borderColor="gray.200"
        align="flex-end"
        w="100%"
        spacing={3}
        minH="44px"
        flexShrink={0}
      >
        {/* Back + editable job name */}
        <HStack minW={0} flex="1" spacing={2} align="center">
          <IconButton
            aria-label="Back to workflows"
            icon={<Icon as={FaArrowLeft} boxSize={4} />}
            variant="ghost"
            size="sm"
            color="gray.700"
            _hover={{ bg: "gray.100" }}
            onClick={onBack}
            flexShrink={0}
          />
          <Icon as={LuWorkflow} boxSize={5} color="purple.700" flexShrink={0} aria-hidden />

          {isEditingJobName ? (
            <HStack flex="1" minW={0} spacing={2} align="center">
              <Input
                flex="1"
                minW={0}
                size="md"
                variant="flushed"
                fontWeight="600"
                color="gray.800"
                autoFocus
                value={jobNameDraft}
                onChange={e => setJobNameDraft(e.target.value)}
                onKeyDown={e => {
                  if (e.key === "Enter") confirmJobNameEdit();
                  else if (e.key === "Escape") setIsEditingJobName(false);
                }}
                borderColor="purple.400"
                _focusVisible={{ borderColor: "purple.500", boxShadow: "none" }}
              />
              <IconButton
                aria-label="Confirm job name"
                icon={<Icon as={FaCheck} boxSize={4} color="green.500" />}
                size="xs"
                variant="ghost"
                colorScheme="green"
                flexShrink={0}
                onMouseDown={e => e.preventDefault()}
                onClick={confirmJobNameEdit}
              />
            </HStack>
          ) : (
            <HStack
              role="group"
              flex="1"
              minW={0}
              spacing={2}
              align="center"
              cursor="pointer"
              onClick={() => {
                setJobNameDraft(jobDisplayName);
                setIsEditingJobName(true);
              }}
              _hover={{ opacity: 0.85 }}
            >
              <Text
                fontSize="md"
                fontWeight="700"
                color="gray.800"
                noOfLines={1}
                mb={0}
                flex="1"
                minW={0}
                textAlign="left"
                borderBottomWidth="2px"
                borderColor="transparent"
                _groupHover={{ borderColor: "purple.200" }}
              >
                {jobDisplayName}
              </Text>
              <Icon as={MdEdit} boxSize={4} color="gray.500" flexShrink={0} />
            </HStack>
          )}
        </HStack>

        {/* Process / Guidance / Past runs tabs + Run button */}
        <HStack spacing={3} flexShrink={0} align="flex-end" pb={0}>
          <Tabs
            variant="line"
            colorScheme="purple"
            index={activeTab}
            onChange={setActiveTab}
            size="sm"
          >
            <TabList borderBottom="none" gap={0} minH="auto" aria-label="Workflow sections">
              <Tab
                fontWeight="600"
                py={2}
                px={3}
                _selected={{ fontWeight: "700", borderBottomWidth: "3px", borderColor: "purple.700" }}
              >
                <HStack as="span" spacing={2}>
                  <Icon as={MdAccountTree} boxSize={5} flexShrink={0} />
                  <Text as="span" mb={0} fontSize="sm">Process</Text>
                </HStack>
              </Tab>
              <Tab
                fontWeight="600"
                py={2}
                px={3}
                _selected={{ fontWeight: "700", borderBottomWidth: "3px", borderColor: "purple.700" }}
              >
                <HStack as="span" spacing={2}>
                  <Icon as={MdCode} boxSize={5} flexShrink={0} />
                  <Text as="span" mb={0} fontSize="sm">Guidance</Text>
                </HStack>
              </Tab>
              <Tab
                fontWeight="600"
                py={2}
                px={3}
                _selected={{ fontWeight: "700", borderBottomWidth: "3px", borderColor: "purple.700" }}
              >
                <HStack as="span" spacing={2}>
                  <Icon as={FaHistory} boxSize={4} flexShrink={0} />
                  <Text as="span" mb={0} fontSize="sm">Past runs</Text>
                </HStack>
              </Tab>
            </TabList>
            {/* Hidden panels — content is rendered below via CSS display toggling */}
            <TabPanels
              display="none"
              position="absolute"
              visibility="hidden"
              aria-hidden
              h={0}
              w={0}
              overflow="hidden"
              p={0}
              m={0}
            >
              <TabPanel p={0} />
              <TabPanel p={0} />
              <TabPanel p={0} />
            </TabPanels>
          </Tabs>

          {selectedJob?.id && (
            <Button
              size="sm"
              bg="purple.700"
              color="white"
              _hover={{ bg: "purple.600" }}
              _active={{ bg: "purple.800" }}
              fontWeight="600"
              isLoading={isRunningWorkflow}
              onClick={handleRunJob}
              mb="2px"
              leftIcon={<Icon as={MdPlayArrow} boxSize={5} />}
            >
              Run
            </Button>
          )}
        </HStack>
      </HStack>

      {/* Content */}
      <Box flex="1" overflow="hidden" display="flex" flexDirection="column" minH={0}>
        <Box
          display={activeTab === 2 ? "none" : "flex"}
          flex="1"
          minH={0}
          overflow="hidden"
          flexDirection="column"
        >
          <JobCreationComponent
            jobName={jobName}
            setJobName={setJobName}
            selectedJob={selectedJob}
            isCreatingNew={isCreatingNew}
            triggerSave={triggerSave}
            setTriggerSave={setTriggerSave}
            triggerDiscard={triggerDiscard}
            setTriggerDiscard={setTriggerDiscard}
            setIsSaving={setIsSaving}
            onSaveSuccess={onBack}
            setHasFormChanges={setHasFormChanges}
            jobFormView={activeTab === 1 ? "guidance" : "process"}
          />
        </Box>
        <Box
          display={activeTab === 2 ? "flex" : "none"}
          flex="1"
          minH={0}
          overflow="hidden"
          flexDirection="column"
          px={2}
        >
          <Box flex="1" minH={0} overflowY="auto" px={6} py={5}>
            <AnalysisRunsViewer selectedJob={selectedJob} />
          </Box>
        </Box>
      </Box>

      {/* Save / Discard footer */}
      {showFormFooter && (
        <Box
          flexShrink={0}
          borderTop="1px"
          borderColor="gray.200"
          py={2}
          px={6}
          bg="white"
        >
          <HStack justify="space-between" align="center">
            <Text fontSize="md" color="gray.700" fontWeight="600" mb={0}>
              You have unsaved changes.
            </Text>
            <HStack spacing={3}>
              <Button
                colorScheme="green"
                size="md"
                onClick={handleSave}
                isLoading={isSaving}
                loadingText={isCreatingNew ? "Saving…" : "Updating…"}
                leftIcon={<Icon as={FaSave} boxSize={4} />}
                fontWeight="600"
                fontSize="md"
              >
                {isCreatingNew ? "Save" : "Update"}
              </Button>
              <Button
                colorScheme="red"
                variant="outline"
                size="md"
                onClick={isCreatingNew ? onBack : handleDiscard}
                isDisabled={isSaving}
                leftIcon={<Icon as={FaTimes} boxSize={4} />}
                fontWeight="600"
                fontSize="md"
              >
                {isCreatingNew ? "Cancel" : "Discard"}
              </Button>
            </HStack>
          </HStack>
        </Box>
      )}
    </Box>
  );
};

export default JobViewComponent;
